import type { PortSuggestOption } from "@/components/common/PortSuggestInput";
import { api } from "@/services/api";
import { portGroupApiToCreate } from "@/lib/allocationPortGroups";
import { portLabel } from "@/lib/portAreaMatchUtils";
import {
  filterAllocationPortGroupsForKai,
  portGroupSuggestLabel,
} from "@/lib/portGroupSuggest";
import {
  buildPortSuggestMenuStructure,
  type PortSuggestAreaGroup,
} from "@/lib/portSuggestGroups";
import type { PortGroupCreate } from "@/features/rates/create/wizardTypes";

import type { KaiEntityTag } from "./kaiEntityTags";

/**
 * Kai `@port` autosuggest — aligned with [PortSuggestInput]: ranked canonical
 * ports first, then mapped-area rows (one canonical label + area hint), then
 * small trade-lane groups when the **group name** matches (never country-scale
 * buckets like "China (94 ports)" on a member-port query).
 */

let portGroupsCache: Promise<PortGroupCreate[]> | null = null;

function loadPortGroups(): Promise<PortGroupCreate[]> {
  if (!portGroupsCache) {
    portGroupsCache = api
      .getPortGroups()
      .then((groups) => groups.map(portGroupApiToCreate))
      .catch((err) => {
        portGroupsCache = null;
        throw err;
      });
  }
  return portGroupsCache;
}

function portOptionToTag(port: PortSuggestOption): KaiEntityTag {
  return {
    kind: "port",
    value: port.locode.trim().toUpperCase(),
    label: portLabel(port.locode, port.name),
    metadata: {
      port_role: "either",
      port_summary: port.country?.trim() || undefined,
    },
  };
}

/** One row per mapped area: canonical port label + hint listing siblings. */
function areaGroupToTag(group: PortSuggestAreaGroup): KaiEntityTag {
  const primary = group.ports[0];
  const label = primary
    ? portLabel(primary.locode, primary.name)
    : group.title;
  const value =
    group.memberLocodes.length > 0
      ? group.memberLocodes.join(",")
      : group.ports.map((p) => p.locode.trim().toUpperCase()).join(",");
  return {
    kind: "port",
    value,
    label,
    metadata: {
      port_role: "either",
      port_summary: `${group.title} · ${group.mappedSummary}`,
    },
  };
}

function allocationGroupToTag(group: PortGroupCreate): KaiEntityTag {
  const { title, subtitle } = portGroupSuggestLabel(group);
  return {
    kind: "port",
    value: group.ports.map((l) => l.trim().toUpperCase()).join(","),
    label: title,
    metadata: {
      port_role: "either",
      port_summary: `Port group · ${subtitle}`,
    },
  };
}

function dedupePortTags(tags: KaiEntityTag[]): KaiEntityTag[] {
  const seen = new Set<string>();
  const out: KaiEntityTag[] = [];
  for (const tag of tags) {
    const key = tag.value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
  }
  return out;
}

export async function searchPortTags(query: string): Promise<KaiEntityTag[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const [groups, ports] = await Promise.all([
    loadPortGroups().catch(() => [] as PortGroupCreate[]),
    api.searchPorts(trimmed).catch(() => []),
  ]);

  const slice = ports.slice(0, 20);
  let structure = buildPortSuggestMenuStructure(slice, null);
  if (slice.length > 0) {
    try {
      const expansion = await api.getPortAreaExpansion(
        slice.map((p) => p.locode).join(","),
      );
      structure = buildPortSuggestMenuStructure(slice, expansion);
    } catch {
      structure = buildPortSuggestMenuStructure(slice, null);
    }
  }

  const standaloneTags = structure.standalone.map(portOptionToTag);
  const areaTags = structure.areaGroups.map(areaGroupToTag);
  const groupTags = filterAllocationPortGroupsForKai(groups, trimmed, 2).map(
    allocationGroupToTag,
  );

  return dedupePortTags([...standaloneTags, ...areaTags, ...groupTags]).slice(
    0,
    8,
  );
}
